import { STORAGE_KEY } from 'constants/browserStorageKeys';
import { DIALOG_END_REASON } from 'constants/chat';
import wsErrors from 'constants/wsErrors';
import wsMessageTypes from 'constants/wsMessageTypes';
import wsStateEvents from 'constants/wsStateEvents';
import { Store } from 'redux';
import { googleAnalytics } from 'services/googleAnalytics';
import { getStorageItem, setStorageItem } from 'services/storage';
import { MessageOwner, MessageStatus } from 'types';
import {
  restartChat,
  saveBotPromptsResponse,
  saveBotTypingOff,
  saveBotTypingOn,
  saveMetaMessage,
  setAgentConnected,
  setAgentConnecting,
  setAgentTimeout,
  setAttachmentsConfig,
  setChatWithAgent,
  setDebugInfo,
  setDialogEnded,
  setDisableChat,
  setFeedback,
  setIsHistory,
  setSensitiveInput,
  setSessionExpired,
  startChatSuccess,
  stopRetryConnect,
  updateDialogId,
  updateThreadMessage,
} from './services/chat/actions';
import { getChatData } from './services/chat/selectors';
import { resetError, setError } from './services/errors';
import {
  getEnableSensitiveInputMask,
  getEnableTransferDisableInput,
  getGoogleAnalyticsEnabled,
  getToken,
} from './services/session/selectors';
import initialChatState from './services/chat/initialState';
import {
  handleMultipleApiUrlError,
  handleSingleApiUrlError,
} from './utils/apiUrlResolver';
import isEmpty from 'lodash/isEmpty';
import {
  getMessage,
  getStatus,
  isDeliveredMessage,
  isMetaMessage,
  isProactiveMessage,
  resolveSensitiveInput,
} from './utils/chatHelpers';

const handleWebSocketOpen = (store: Store) => {
  const { chat } = store.getState();

  if (chat.isRetrying) {
    store.dispatch(stopRetryConnect());
    store.dispatch(resetError());
  }
};

const handleWebSocketMessage = (
  store: Store<any, any>,
  event: MessageEvent,
  ws,
) => {
  const data = JSON.parse(event.data);
  const {
    type,
    session_id,
    dialog_id,
    is_history,
    dialog_response,
    transfer_info,
    source,
    sequence_id,
    utterance,
    client_message_id,
    error_code,
    state,
    typing_on,
    metadata,
    files,
    debug_info,
  } = data;
  const { session, chat } = store.getState();
  const gaEnabled = getGoogleAnalyticsEnabled(store.getState());

  store.dispatch(setIsHistory(!!is_history));

  setStorageItem(STORAGE_KEY.PREVIOUS_DIALOG_ID, dialog_id);
  if (transfer_info?.transfer_id) {
    setStorageItem(STORAGE_KEY.TRANSFER_ID, transfer_info?.transfer_id);
  }

  if (type === wsMessageTypes.START_SESSION_RESPONSE) {
    setStorageItem(STORAGE_KEY.SESSION_ID, session_id);
  }

  if (type === wsMessageTypes.STATE_EVENT) {
    store.dispatch(saveBotTypingOff());

    if (state === wsStateEvents.DIALOG_END) {
      if (!session.config.enableConsecutiveDialogs) {
        store.dispatch(setDisableChat(true));
      }

      store.dispatch(setDialogEnded(true));
      store.dispatch(setAgentConnecting(false));

      if (
        getEnableTransferDisableInput(store.getState()) &&
        metadata?.reason === DIALOG_END_REASON.QUEUE_WAIT_TIMEOUT
      ) {
        store.dispatch(setAgentTimeout(true));
      }

      if (
        !getStorageItem(STORAGE_KEY.VIEW_FEEDBACK) &&
        session.config?.feedback?.type
      ) {
        store.dispatch(setFeedback());
      }

      store.dispatch(setChatWithAgent(false));
      store.dispatch(setAttachmentsConfig(initialChatState.attachmentsConfig));
    }

    if (state === wsStateEvents.TRANSFERRED) {
      const { onAgentTransfer } = googleAnalytics(gaEnabled);
      onAgentTransfer(window.location.href);

      store.dispatch(
        setAttachmentsConfig({
          allowedTypes: metadata?.allowed_types || [],
          attachmentsEnabled: metadata?.attachments_enabled,
          attachmentsRichEnabled: false,
          maxFileSize: metadata?.max_file_size || 0,
          maxFiles: 1,
        }),
      );
    }

    if (getEnableTransferDisableInput(store.getState())) {
      if (state === wsStateEvents.TRANSFERRED_DISABLE_INPUT) {
        store.dispatch(setAgentConnecting(true));
      }

      if (state === wsStateEvents.AGENT_JOINED) {
        store.dispatch(setAgentConnecting(false));
        store.dispatch(setAgentConnected(true));
      }
    }

    if (state === wsStateEvents.SESSION_EXPIRED) {
      store.dispatch(setSessionExpired());
    }
  }

  if (type === wsMessageTypes.ATTACHMENT_EVENT) {
    if (isDeliveredMessage(chat.data.thread, data)) {
      store.dispatch(updateThreadMessage(data));
    } else {
      store.dispatch(
        saveBotPromptsResponse({
          message: {
            utterance,
            files,
          },
          status: MessageStatus.SENT,
          type: 'file',
          sequence_id,
          client_message_id,
          source,
        }),
      );
    }
  }

  if (type === wsMessageTypes.DIALOG_MESSAGE_EVENT) {
    store.dispatch(saveBotTypingOff());
    store.dispatch(setChatWithAgent(source === MessageOwner.AGENT));

    if (!chat.isLoaded) {
      store.dispatch(startChatSuccess(dialog_id));
    }

    if (chat.isDialogEnded) {
      store.dispatch(setDialogEnded(false));
    }

    if (isDeliveredMessage(chat.data.thread, data)) {
      // UI creates a new message and when it receives delivery confirmation from the server, it updates the message
      store.dispatch(updateThreadMessage(data));
    } else if (isMetaMessage(dialog_response)) {
      store.dispatch(saveMetaMessage(null));
      store.dispatch(saveMetaMessage(dialog_response.ui_component));
    } else if (!isProactiveMessage(sequence_id, source)) {
      const message = getMessage(source, dialog_response, utterance);

      const isInputDisabled =
        message?.free_text_enabled === false ||
        message?.content?.free_text_enabled === false;

      const sensitiveEnabled = getEnableSensitiveInputMask(store.getState());
      const sensitiveResolved = resolveSensitiveInput(data);
      const isSensitiveMessage =
        sensitiveEnabled && source === MessageOwner.USER && sensitiveResolved;

      // Footer password mode is driven by the bot prompt asking for sensitive
      // input, not by USER messages. Restoring a flagged USER message from
      // history must not flip the footer into password mode.
      store.dispatch(
        setSensitiveInput(
          sensitiveEnabled && source !== MessageOwner.USER && sensitiveResolved,
        ),
      );

      store.dispatch(
        saveBotPromptsResponse({
          message,
          status: getStatus(source, client_message_id),
          type: dialog_response?.ui_component ? 'ui_component' : 'message',
          sequence_id,
          client_message_id,
          source,
          isSensitive: isSensitiveMessage,
        }),
      );

      if (message?.type === 'ATTACHMENT') {
        store.dispatch(
          setAttachmentsConfig({
            allowedTypes: message?.types_allowed || [],
            attachmentsEnabled: false,
            attachmentsRichEnabled: false,
            maxFileSize: message?.max_size || 0,
            maxFiles: message?.max_attachments || 0,
          }),
        );
      } else {
        store.dispatch(
          setAttachmentsConfig({
            attachmentsRichEnabled: false,
          }),
        );
      }

      store.dispatch(setDisableChat(isInputDisabled));
    }

    // transfer_info
    store.dispatch(updateDialogId(dialog_id));
    store.dispatch(setDebugInfo(debug_info));
  }

  if (type === wsMessageTypes.TYPING_EVENT) {
    if (typing_on) {
      store.dispatch(saveBotTypingOn());
    } else {
      store.dispatch(saveBotTypingOff());
    }
  }

  if (type === wsMessageTypes.ERROR_EVENT) {
    if (error_code === wsErrors.SESSION_NOT_FOUND) {
      store.dispatch(restartChat());
    } else if (error_code === wsErrors.MESSAGE_REJECTED) {
      const rejectedMessage = chat.data.thread.find(
        msg => msg.client_message_id === client_message_id,
      );

      store.dispatch(
        updateThreadMessage({
          ...rejectedMessage,
          status: MessageStatus.ERROR,
        }),
      );
    } else if (error_code === wsErrors.DIALOG_NOT_FOUND) {
      const attachedData = getChatData(store.getState());
      ws.send(
        JSON.stringify({
          type: wsMessageTypes.START_DIALOG_REQUEST,
          api_key: getToken(store.getState()),
          session_id: getStorageItem(STORAGE_KEY.SESSION_ID),
          input_fields: attachedData,
        }),
      );
    } else {
      store.dispatch(setError(error_code));
    }
  }
};

const handleWebSocketError = (
  store: Store,
  apiUrls: string[],
  urlSelectMethod: string,
  ws,
) => {
  store.dispatch(setError({ message: 'errors.generalError' }));
  if (apiUrls?.length) {
    handleMultipleApiUrlError(apiUrls, urlSelectMethod, store, ws);
  } else {
    handleSingleApiUrlError(store, ws);
  }
  console.log(event, 'ws has error');
};

const handleWebSocketClose = (store: Store, ws) => {
  const { session, chat, errors } = store.getState();

  const lastMsgSequenceId =
    chat.data.thread[chat.data.thread.length - 1]?.sequence_id;
  if (
    !chat.isDialogEnded &&
    chat.isOpen &&
    isEmpty(errors) &&
    lastMsgSequenceId
  ) {
    ws.send(
      JSON.stringify({
        type: wsMessageTypes.SESSION_RESUME_REQUEST,
        api_key: session.token,
        session_id: getStorageItem(STORAGE_KEY.SESSION_ID),
        from_sequence_id: lastMsgSequenceId + 1,
      }),
    );
  }
};

export const wsHandlers = {
  onOpen: store => handleWebSocketOpen(store),
  onMessage: (store, event, ws) => handleWebSocketMessage(store, event, ws),
  onError: (store, apiUrls, urlSelectMethod, ws) =>
    handleWebSocketError(store, apiUrls, urlSelectMethod, ws),
  onClose: (store, ws) => handleWebSocketClose(store, ws),
};
